/**
 * outpost_props_loader.ts — the generated outpost props, loaded once and
 * cloned into bases (the Hugging Face outpost wave, 2026-09-17). The same
 * shape as ceremony_props_loader: `prefetchOutpostProps(app)` reads
 * `public/props/outpost/manifest.json` and parses every listed GLB through
 * AssetModule's loader into a MASTER per key; `outpostProp(key)` hands the
 * OutpostModule a fitted CLONE — seated by its bounding box bottom-centre,
 * scaled to the catalog height or to a span LENGTH along local X — or null
 * when the key is not cooked / not landed yet. Clones share the master's
 * geometry and materials (`userData.sharedAsset`).
 */
import * as THREE from 'three/webgpu';
import type { App } from 'enginex/core/App.js';
import { Logger } from 'enginex/core/Logger.js';
import { fitTransform, type CeremonyManifest } from '../../lib/ceremony_props.js';
import { OUTPOST_PROPS, outpostPropUrl, TURRET_HEAD_BARREL } from '../../lib/outpost_props.js';

interface Master { object: THREE.Object3D; fit: { scale: number; dx: number; dy: number; dz: number }; sizeM: [number, number, number] }

const masters = new Map<string, Master>();
const stats = { manifest: false, keys: 0, loaded: 0, failed: 0, clones: 0, error: '' };
let started: Promise<void> | null = null;

/** Diag: which props are in (`__outpostProps()`). */
export function outpostPropStats(): typeof stats & { ready: string[] } {
  return { ...stats, ready: [...masters.keys()] };
}

export function prefetchOutpostProps(app: App): Promise<void> {
  if (started) return started;
  started = (async () => {
    (window as unknown as Record<string, unknown>).__outpostProps = outpostPropStats;
    const assets = app.getModule('AssetModule') as { loadUrl?(url: string): Promise<THREE.Object3D> } | undefined;
    if (!assets?.loadUrl) { stats.error = 'no AssetModule'; return; }
    let manifest: CeremonyManifest;
    try {
      const res = await fetch(outpostPropUrl('manifest.json', document.baseURI), { cache: 'no-cache' });
      if (!res.ok) { stats.error = `manifest ${res.status}`; return; }
      manifest = await res.json() as CeremonyManifest;
    } catch (e) { stats.error = `manifest: ${String((e as Error)?.message ?? e)}`; return; }
    stats.manifest = true;
    const entries = Object.entries(manifest.props ?? {}).filter(([key]) => key in OUTPOST_PROPS);
    stats.keys = entries.length;
    for (const [key, ent] of entries) {
      try {
        const obj = await assets.loadUrl(outpostPropUrl(ent.file, document.baseURI));
        obj.traverse((o) => { o.userData.sharedAsset = true; });
        const box = new THREE.Box3().setFromObject(obj);
        const fit = fitTransform(box, OUTPOST_PROPS[key].targetH);
        masters.set(key, { object: obj, fit, sizeM: [box.max.x - box.min.x, box.max.y - box.min.y, box.max.z - box.min.z] });
        stats.loaded++;
      } catch (e) {
        stats.failed++;
        Logger.warn('OutpostProps', `${key}: ${String((e as Error)?.message ?? e)}`);
      }
    }
    Logger.info('OutpostProps', `${stats.loaded}/${stats.keys} generated outpost props ready`);
  })();
  return started;
}

/** The fitted size (m) of a generated prop at its catalog height, or null. */
export function outpostPropSize(key: string): [number, number, number] | null {
  const m = masters.get(key);
  return m ? [m.sizeM[0] * m.fit.scale, m.sizeM[1] * m.fit.scale, m.sizeM[2] * m.fit.scale] : null;
}

/**
 * A fitted clone of a generated outpost prop, or null (keep the previous
 * piece). The group's origin is the seat (bounding box bottom-centre) and
 * `yaw` turns it about that seat. `len` scales the model's LONGEST horizontal
 * side to that length and lays it along local X (the wall panels); the
 * turret head is turned so its barrel runs +Z (TURRET_HEAD_BARREL).
 */
export function outpostProp(key: string, yaw = 0, opts: { len?: number } = {}): THREE.Group | null {
  const m = masters.get(key);
  if (!m) return null;
  const g = new THREE.Group();
  g.name = `outpost-prop:${key}`;
  g.userData.sharedAsset = true;
  const clone = m.object.clone(true);
  clone.traverse((o) => { o.userData.sharedAsset = true; });
  const longX = m.sizeM[0] >= m.sizeM[2];
  const longest = Math.max(m.sizeM[0], m.sizeM[2], 0.01);
  const scale = opts.len ? opts.len / longest : m.fit.scale;
  clone.scale.setScalar(scale);
  // Re-seat for whichever scale applies (fit.d* were computed at fit.scale).
  const k = scale / m.fit.scale;
  clone.position.set(m.fit.dx * k, m.fit.dy * k, m.fit.dz * k);
  const seated = new THREE.Group();
  seated.userData.sharedAsset = true;
  seated.add(clone);
  if (opts.len && !longX) seated.rotation.y = Math.PI / 2; // the long side along local X
  if (key === 'outpost_turret_head') {
    const turn = TURRET_HEAD_BARREL === 'z' ? 0 : TURRET_HEAD_BARREL === '-z' ? Math.PI : TURRET_HEAD_BARREL === 'x' ? Math.PI / 2 : -Math.PI / 2;
    seated.rotation.y = turn;
  }
  g.add(seated);
  g.rotation.y = yaw;
  stats.clones++;
  return g;
}
